import React, { useEffect, useMemo } from 'react';
import { MultiSelect } from 'primereact/multiselect';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';

// Panel se kači na body da ga dijalog ne bi isekao
const appendToBody = typeof document !== 'undefined' ? document.body : undefined;

export type PlacementRowOption = {
  rowId: string;
  rowName: string;
  locationId: string;
  locationName: string;
  // Koliko paleta je moguće staviti u red (proizvodnja) ili uzeti iz reda (transport)
  available: number;
  capacity?: number;
};

export type FormPlacement = { rowId: string; pallets: number };

type Props = {
  rows: PlacementRowOption[];
  locationIds: string[];
  placements: FormPlacement[];
  needed: number;
  autoDistribute: boolean;
  availableLabel: string;
  title: string;
  onLocationIdsChange: (locationIds: string[]) => void;
  onPlacementsChange: (placements: FormPlacement[]) => void;
  onAutoDistributeChange: (value: boolean) => void;
};

// Popunjava redove redom do raspoloživog broja paleta; zadržane stavke se ne menjaju
export function distributePallets(
  rowIds: string[],
  needed: number,
  rowsById: Map<string, PlacementRowOption>,
  keep: FormPlacement[] = []
): FormPlacement[] {
  let remaining = needed - keep.reduce((sum, p) => sum + p.pallets, 0);
  return rowIds.map((rowId) => {
    const kept = keep.find((p) => p.rowId === rowId);
    if (kept) return kept;
    const take = Math.max(0, Math.min(rowsById.get(rowId)?.available || 0, remaining));
    remaining -= take;
    return { rowId, pallets: take };
  });
}

const LagerPlacementPicker: React.FC<Props> = ({
  rows,
  locationIds,
  placements,
  needed,
  autoDistribute,
  availableLabel,
  title,
  onLocationIdsChange,
  onPlacementsChange,
  onAutoDistributeChange
}) => {
  const rowsById = useMemo(() => new Map(rows.map((r) => [r.rowId, r])), [rows]);

  const locationOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of rows) if (!seen.has(r.locationId)) seen.set(r.locationId, r.locationName);
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [rows]);

  const rowGroups = useMemo(
    () =>
      locationOptions
        .filter((l) => locationIds.includes(l.value))
        .map((l) => ({
          label: l.label,
          items: rows
            .filter((r) => r.locationId === l.value)
            .map((r) => ({
              label: `${r.rowName} (${availableLabel} ${r.available}${r.capacity !== undefined ? `/${r.capacity}` : ''})`,
              value: r.rowId,
              available: r.available
            }))
        })),
    [locationOptions, locationIds, rows, availableLabel]
  );

  const assigned = placements.reduce((sum, p) => sum + Number(p.pallets || 0), 0);

  // Automatska raspodela prati promenu potrebnog broja paleta, izabranih redova i raspoloživosti
  const selectedRowKey = placements.map((p) => p.rowId).join(',');
  useEffect(() => {
    if (!autoDistribute || !selectedRowKey) return;
    const next = distributePallets(selectedRowKey.split(','), needed, rowsById);
    const changed = next.some((p, i) => p.pallets !== placements[i]?.pallets);
    if (changed) onPlacementsChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDistribute, needed, selectedRowKey, rowsById]);

  const handleLocationsChange = (ids: string[]) => {
    onLocationIdsChange(ids);
    onPlacementsChange(placements.filter((p) => ids.includes(rowsById.get(p.rowId)?.locationId || '')));
  };

  const handleRowsChange = (rowIds: string[]) => {
    const keep = autoDistribute ? [] : placements.filter((p) => rowIds.includes(p.rowId));
    onPlacementsChange(distributePallets(rowIds, needed, rowsById, keep));
  };

  const updatePallets = (rowId: string, pallets: number) => {
    onPlacementsChange(placements.map((p) => (p.rowId === rowId ? { ...p, pallets } : p)));
  };

  return (
    <>
      <div className="col-12 md:col-6">
        <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Lokacije</label>
        <MultiSelect
          value={locationIds}
          options={locationOptions}
          optionLabel="label"
          optionValue="value"
          placeholder={locationOptions.length ? 'Izaberite lokacije' : 'Nema dostupnih lokacija'}
          disabled={!locationOptions.length}
          display="chip"
          filter
          appendTo={appendToBody}
          className="w-full"
          panelStyle={{ maxWidth: '96vw' }}
          scrollHeight="260px"
          onChange={(e) => handleLocationsChange(e.value || [])}
        />
      </div>

      <div className="col-12 md:col-6">
        <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Redovi</label>
        <MultiSelect
          value={placements.map((p) => p.rowId)}
          options={rowGroups}
          optionLabel="label"
          optionValue="value"
          optionGroupLabel="label"
          optionGroupChildren="items"
          optionDisabled={(o: { available: number; value: string }) => o.available <= 0 && !placements.some((p) => p.rowId === o.value)}
          placeholder={locationIds.length ? 'Izaberite redove' : 'Prvo izaberite lokacije'}
          disabled={!locationIds.length}
          selectedItemsLabel="{0} reda izabrano"
          maxSelectedLabels={2}
          appendTo={appendToBody}
          className="w-full"
          panelStyle={{ maxWidth: '96vw' }}
          scrollHeight="260px"
          onChange={(e) => handleRowsChange(e.value || [])}
        />
      </div>

      {placements.length > 0 && (
        <div className="col-12">
          <div className="flex justify-content-between align-items-center" style={{ marginBottom: '0.5rem' }}>
            <span style={{ fontWeight: 600 }}>{title}</span>
            <label className="flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
              <span>Automatska raspodela</span>
              <InputSwitch checked={autoDistribute} onChange={(e) => onAutoDistributeChange(!!e.value)} />
            </label>
          </div>
          {placements.map((p) => {
            const info = rowsById.get(p.rowId);
            const over = info ? p.pallets > info.available : false;
            return (
              <div key={p.rowId} className="grid align-items-center" style={{ marginBottom: '0.25rem' }}>
                <div className="col-7 md:col-8">
                  {info ? `${info.locationName} / ${info.rowName}` : 'Nepoznat red'}
                  <small style={{ color: over ? '#ef4444' : '#64748b', marginLeft: '0.5rem' }}>
                    {availableLabel} {info?.available ?? 0}
                  </small>
                </div>
                <div className="col-5 md:col-4">
                  <InputNumber
                    value={p.pallets}
                    onValueChange={(e) => updatePallets(p.rowId, Number(e.value || 0))}
                    min={0}
                    max={info?.available}
                    disabled={autoDistribute}
                    useGrouping={false}
                    suffix=" pal."
                    className={over ? 'p-invalid' : undefined}
                  />
                </div>
              </div>
            );
          })}
          <small style={{ color: assigned === needed ? '#22c55e' : '#ef4444' }}>
            Raspoređeno {assigned} od {needed} paleta
          </small>
        </div>
      )}
    </>
  );
};

export default LagerPlacementPicker;
